// The cabinet at apex-minds.org/cabinet/… — router and shell.
//
// Three modes, decided by the signed-in person's role in the database:
//   student   /cabinet (Навчання) · <subject>[/lesson/<n>[/<k>|hw|work|cards] |
//             /cards | /group | /progress | /p/<slug>] · help (old /hw/<id> → the lesson's /hw)
//             (pages/student.js: the screens follow the student's tree, no tab bar)
//   everyone  /cabinet/profile (pages/profile.js): picture, name, sign-out
//             — the avatar in the top bar links there (no dropdown)
//   teacher   /cabinet/teacher (Викладання: the subjects) · <subject> · <subject>/<group>[/journal[/<s>] |
//             /lesson/<n>[/journal | /hw[/<work>] | /view]] · help · program[/<subject>[/lesson/<n>]]
//             (a subject's lessons, read-only); one tree, no tab bar (user 2026-10-12);
//             addresses by name, not id (js/cabinet/addresses.js)
//   admin     /cabinet/admin (analytics) · groups · admission · people · grading (the AI's
//             homework checks: grading[/<review>|/schemes]) — and all teacher pages
//   editor    («Редактор») admin mode without admission, settings, grading and analytics
//             (pages/admin.js ADMIN_ONLY; the database refuses those to editors)
//
// Mobile first: staff get a bottom tab bar on a phone and a top bar from 900px;
// a student's navigation is the tree itself. The top bar is √ · (staff: role badge) ·
// «Навчання» · «Усі задачі» · «Добірка» · avatar (phones: √ · avatar); no back button.
// Every page, staff and student, walks up the tree by ui.js crumbs().
import * as api from './api.js';
import { esc, navigate, loading, failure, toast, initials, nick, setOnAir, crowned } from './ui.js';
import { icon } from '../util/icons.js';
import { addressBook, resolve, groupHref, lessonHref, hwHref, workHref } from './addresses.js';

// Staff nav keys → the shared icon names; unknown keys fall back to the «Ще» icon ('more').
const NAV_ICON = {
    plan: 'list', lesson: 'monitor', home: 'overview', progress: 'bars', more: 'more', group: 'group',
    review: 'review', tutor: 'help', chart: 'chart', people: 'students', door: 'log-out', event: 'calendar',
    settings: 'settings', cards: 'cards', bank: 'bank',
};
const svg = (k) => icon(NAV_ICON[k] || 'more', { size: 22 });

const NAV = {
    // Students: no tab bar. Their screens follow the tree (предмет → lesson); the
    // few global pages sit on the profile page (avatar → /cabinet/profile).
    student: [],
    applicant: [],
    // Teachers: no second header either (user 2026-10-12). Their screens follow the
    // tree too (Навчання → предмет → група → заняття); what cut across groups
    // («Що зробити», the programme, the help) sits on the subject's page.
    teacher: [],
    // Admin: five sections by job, tabs inside each (pages/admin.js SECTIONS). On a
    // phone the first three stay in the tab bar and «Ще» holds the other two.
    admin: [['/cabinet/admin', 'Огляд', 'home'], ['/cabinet/admin/program', 'Програма', 'group'], ['/cabinet/admin/students', 'Учні', 'people'], ['/cabinet/admin/grading', 'Перевірка ДЗ', 'review'], ['/cabinet/admin/analytics/launch', 'Аналітика', 'chart']],
    // Editor: programmes, groups and people; no admission (Відбір) or analytics.
    editor: [['/cabinet/admin', 'Огляд', 'home'], ['/cabinet/admin/program', 'Програма', 'group'], ['/cabinet/admin/students/all', 'Учні', 'people']],
};

// Each role downloads only its own screens: a student never loads the
// teacher's or the admin's (120 KB they would never run).
const PAGE = { auth: () => import('./pages/auth.js'), student: () => import('./pages/student.js'), teacher: () => import('./pages/teacher.js'), admin: () => import('./pages/admin.js') };
async function load(...keys) {
    const mods = await Promise.all(keys.map(k => PAGE[k]()));
    return Object.fromEntries(keys.map((k, i) => [k, mods[i]]));
}
// The screens this address most likely needs, fetched while the sign-in is
// checked rather than after it (a guess: the role still decides what runs).
// Every other /cabinet/<subject>/… address is a student's (pages/student.js).
const OTHER = /^\/cabinet\/(login|profile|settings|privacy|consent|waitlist|events|help)(\/|$)/;
function warm(path) {
    const keys = path.startsWith('/cabinet/admin') ? ['admin', 'teacher']
        : path.startsWith('/cabinet/teacher') ? ['teacher']
        : path === '/cabinet/login' ? ['auth'] : path === '/cabinet' || !OTHER.test(path) ? ['student'] : [];
    keys.forEach(k => PAGE[k]().catch(() => {}));
}

async function route() {
    const path = location.pathname.replace(/\/+$/, '') || '/cabinet';
    try {
        // A parent's consent link works without signing in.
        const cm = path.match(/^\/cabinet\/consent\/([0-9a-f]{20,64})$/);
        // The waiting list's letter: leaving it works without signing in too.
        const wm = path.match(/^\/cabinet\/waitlist\/([0-9a-f]{20,64})$/);
        if (!cm && !wm && path !== '/cabinet/privacy') warm(path);
        await api.init((step) => { const b = document.getElementById('boot'); if (b) b.textContent = step; });
        if (cm) { paint(null); return (await import('./pages/consent.js')).consent(cm[1]); }
        if (wm) { paint(null); return (await import('./pages/waitlist.js')).leaveByLink(wm[1]); }
        if (path === '/cabinet/privacy') { paint(null); return (await import('./pages/privacy.js')).privacy(); }
        // «Інший демо-користувач»: sign out before deciding where to go.
        if (path === '/cabinet/login' && new URLSearchParams(location.search).has('switch')) { await api.signOut(); forgetMarker(); history.replaceState(null, '', '/cabinet/login'); }
        const m = await api.me();
        if (!m || !m.profile) {
            forgetMarker();
            paint(null);
            return path === '/cabinet/login' ? (await load('auth')).auth.login(m) : navigate('/cabinet/login', { replace: true });
        }
        if (path === '/cabinet/login') return navigate('/cabinet', { replace: true });
        api.logVisit(path);
        paint(m);

        // The profile page is everyone's (students, applicants, teachers, admins).
        if (path === '/cabinet/profile') return (await import('./pages/profile.js')).profile(m);
        if (m.role === 'student' && path === '/cabinet/settings') return navigate('/cabinet/profile', { replace: true });
        // Editors run programmes, groups and people: admin mode (pages/admin.js hides what stays the admin's).
        const runs = api.runsGroups(m.role);
        if (runs && path.startsWith('/cabinet/admin')) return await adminRoute(await load('admin', 'teacher'), path, m);
        if ((m.role === 'teacher' || runs) && path.startsWith('/cabinet/teacher')) return await teacherRoute(await load('teacher'), path);
        if (runs && (path === '/cabinet')) return navigate('/cabinet/admin', { replace: true });
        if (m.role === 'teacher' && (path === '/cabinet' || !path.startsWith('/cabinet/teacher'))) return navigate('/cabinet/teacher', { replace: true });
        if (m.role !== 'student') return navigate(runs ? '/cabinet/admin' : '/cabinet/teacher', { replace: true });
        return await studentRoute(await load('student'), path, m);
    } catch (e) {
        failure(e);
    }
}

async function adminRoute(P, path, m) {
    const A = P.admin;
    A.forRole(m.role);
    addressBook().catch(() => {});   // the pages' group and lesson links, beside their data
    if (m.role !== 'admin' && A.adminOnly(path)) return navigate('/cabinet/admin/program', { replace: true });
    const R = {
        '/cabinet/admin': A.overview,
        '/cabinet/admin/program': A.program, '/cabinet/admin/program/lessons': A.lessons, '/cabinet/admin/program/team': A.team,
        '/cabinet/admin/program/settings': async () => (await import('./pages/settings.js')).settings(A.tabs('program', '/cabinet/admin/program/settings')), '/cabinet/admin/program/bank': () => P.teacher.bank({ adminTabs: A.tabs('program', '/cabinet/admin/program/bank') }),
        '/cabinet/admin/students': async () => (await import('./pages/selection.js')).list(A.tabs('students', '/cabinet/admin/students')),
        '/cabinet/admin/students/allocation': A.students, '/cabinet/admin/students/all': () => A.allStudents(), '/cabinet/admin/students/waitlist': A.waitlist, '/cabinet/admin/students/mail': A.mailBlocked,
        '/cabinet/admin/analytics/launch': A.launch, '/cabinet/admin/analytics': () => A.analytics('topics'), '/cabinet/admin/analytics/use': () => A.analytics('use'), '/cabinet/admin/analytics/spend': A.spend,
        '/cabinet/admin/grading': A.grading, '/cabinet/admin/grading/schemes': A.gradingSchemes,
    };
    const gm = path.match(/^\/cabinet\/admin\/grading\/([0-9a-f-]{36})$/);
    if (gm) return A.gradingReview(gm[1]);   // one AI check, every step of it
    const am = path.match(/^\/cabinet\/admin\/students\/a\/([0-9a-f-]{36})$/);
    if (am) return (await import('./pages/selection.js')).card(A.tabs('students', '/cabinet/admin/students'), am[1]);
    const pm = path.match(/^\/cabinet\/admin\/program\/([a-z0-9][a-z0-9-]*)\/lesson\/(\d+)$/);
    if (pm) return A.topicAt(pm[1], +pm[2]);   // a subject's lesson n, read-only
    const tm = path.match(/^\/cabinet\/admin\/program\/lessons\/([0-9a-f-]{36})$/);
    if (tm) return A.topic(tm[1]);   // read-only, like the list
    return R[path] ? R[path]() : navigate('/cabinet/admin', { replace: true });
}

async function teacherRoute(P, path) {
    let mm;
    if (path === '/cabinet/teacher') return P.teacher.home();
    // The subject's programme is gone (user 2026-10-12): every lesson is on each group's
    // page. Old links land on the subject.
    if ((mm = path.match(/^\/cabinet\/teacher\/program(?:\/([a-z0-9][a-z0-9-]*))?(?:\/lesson\/\d+)?$/))) return navigate(mm[1] && !/^[0-9a-f-]{36}$/.test(mm[1]) ? `/cabinet/teacher/${mm[1]}` : '/cabinet/teacher', { replace: true });
    // «Перевірка» sits on each subject's «Що зробити» since 2026-10-12.
    if (path === '/cabinet/teacher/review') return navigate('/cabinet/teacher', { replace: true });
    // The bank tab is gone (user 2026-10-12): it sits on each lesson's «ДЗ» step.
    if (path === '/cabinet/teacher/bank') return navigate('/cabinet/teacher', { replace: true });
    if (path === '/cabinet/teacher/help') return P.teacher.help();
    // By id: the addresses before 2026-10-12, and whatever the address book cannot name.
    // One it can name moves to that address (js/cabinet/addresses.js).
    const byId = async (id, href, open) => { await addressBook().catch(() => null); const to = href(id);
        return to.includes(id) ? open() : navigate(to + location.hash, { replace: true }); };
    if ((mm = path.match(/^\/cabinet\/teacher\/g\/([0-9a-f-]{36})$/))) return byId(mm[1], groupHref, () => P.teacher.group(mm[1]));
    if ((mm = path.match(/^\/cabinet\/teacher\/lesson\/([0-9a-f-]{36})$/))) return byId(mm[1], lessonHref, () => P.teacher.lesson(mm[1], ['materials', 'journal', 'hw', 'view'].includes(location.hash.slice(1)) ? location.hash.slice(1) : ''));
    if ((mm = path.match(/^\/cabinet\/teacher\/hw\/([0-9a-f-]{36})$/))) return byId(mm[1], hwHref, () => P.teacher.homework(mm[1]));
    if ((mm = path.match(/^\/cabinet\/teacher\/hw\/([0-9a-f-]{36})\/([0-9a-f-]{36})$/))) return byId(mm[1], (a) => workHref(a, mm[2]), () => P.teacher.gradeWork(mm[1], mm[2]));
    // /cabinet/teacher/<subject>[/<group>[/journal[/<s>] | /lesson/<n>[/journal | /hw[/<work>] | /view]]]
    const at = await resolve(path);
    if (at?.subject) return P.teacher.subject(at.subject);
    // «Учні групи» became «Журнал групи» (2026-10-14): the old address leads there.
    if (at?.group && at.tab === 'students') return navigate(path.replace(/\/students$/, '/journal') + location.hash, { replace: true });
    if (at?.group) return at.tab === 'journal' ? P.teacher.groupJournal(at.group, at.pupil) : P.teacher.group(at.group);
    if (at?.work) return P.teacher.gradeWork(at.hw, at.work);
    // A lesson's second or third set (/hw-2, /hw-3) opens its «ДЗ» with that set in view.
    if (at?.lesson) return P.teacher.lesson(at.lesson, at.tab || '', /\/hw-\d+$/.test(path) ? at.hw : null);
    return navigate('/cabinet/teacher', { replace: true });
}

async function studentRoute(P, path, m) {
    const S = P.student;
    // Students come through selection (admin «Відбір»): an account without a
    // group is not (yet) in the programme.
    if (!m.track) return S.notEnrolled(m);
    return S.route(path, m);
}

// ── Shell ────────────────────────────────────────────────────────────────
function navKey(m) {
    if (!m) return null;
    if (m.role === 'admin') return 'admin';
    if (m.role === 'editor') return 'editor';
    if (m.role === 'teacher') return 'teacher';
    return m.track ? 'student' : 'applicant';
}

const HOME_LABEL = { teacher: 'Викладання', admin: 'Управління' };
function paint(m) {
    setOnAir('');
    const key = navKey(m);
    const items = NAV[key] || [];
    const here = location.pathname.replace(/\/+$/, '');
    const admins = key === 'admin' || key === 'editor';
    const active = (href) => here === href || (here.startsWith(href + '/') && !['/cabinet/teacher', '/cabinet/admin'].includes(href))
        || (admins && href === '/cabinet/admin/program' && here.startsWith('/cabinet/teacher'))
        || (key === 'editor' && href === '/cabinet/admin/students/all' && here.startsWith('/cabinet/admin/students'))
        || (key === 'admin' && href.startsWith('/cabinet/admin/analytics') && here.startsWith('/cabinet/admin/analytics'));
    const link = ([href, label, icon]) => `<a href="${href}" class="${active(href) ? 'is-active' : ''}" ${active(href) ? 'aria-current="page"' : ''} data-track="none">${svg(icon)}<span>${esc(label)}</span></a>`;
    document.getElementById('navTop').innerHTML = items.map(link).join('');
    // Phone: four slots at most. Five share 375px at 75px each and a label wraps
    // («Перевірка ДЗ»), so with more than four destinations the first three stay and
    // «Ще» (the bottom sheet) holds the rest. The top bar from 1024px shows them all.
    const bottom = items.length > 4 ? items.slice(0, 3) : items;
    const rest = items.length > 4 ? items.slice(3) : [];
    document.getElementById('navBottom').innerHTML = bottom.map(link).join('') +
        (rest.length ? `<button type="button" data-track="none" class="${rest.some(r => active(r[0])) ? 'is-active' : ''}" id="moreBtn">${svg('more')}<span>Ще</span></button>` : '');
    document.getElementById('navBottom').hidden = !items.length;
    document.body.classList.toggle('has-tabbar', !!items.length);
    document.getElementById('moreBtn')?.addEventListener('click', () => openMenu(m, rest, true));

    const badge = document.getElementById('modeBadge');
    badge.textContent = !m ? 'Навчання' : m.role === 'admin' ? 'Адмін' : m.role === 'editor' ? 'Редактор' : m.role === 'teacher' ? 'Вчитель' : 'Учень';
    // Staff only: a student or a signed-out visitor sees just the header (user 2026-10-11).
    badge.hidden = !['admin', 'editor', 'teacher'].includes(m?.role);
    badge.classList.toggle('is-staff', !badge.hidden);
    // Home is named for the work (user 2026-10-13): a teacher's «Викладання», an
    // admin's «Управління», everyone else's «Навчання».
    const home = HOME_LABEL[m?.role] || 'Навчання';
    document.querySelector('.cab-hlink[data-track-link-id="learning"]').lastChild.nodeValue = home;
    // √ is home (/cabinet) once signed in; signed out the cabinet's home is the
    // sign-in page itself, so √ goes to the public home instead.
    const brand = document.querySelector('.cab-top .site-brand');
    brand.href = m?.profile ? '/cabinet' : '/';
    brand.setAttribute('aria-label', m?.profile ? `Apex Minds: ${home}` : 'Apex Minds');
    for (const id of ['navTop', 'navBottom']) document.getElementById(id).setAttribute('aria-label', home);
    document.title = `${home} · Apex Minds`;
    // Signed in: the site links and the avatar ([data-signed-in], styles.apex.css).
    // cabinet.html sets the same class from the marker before first paint.
    const signedIn = !!m?.profile;
    document.documentElement.classList.toggle('is-signed-in', signedIn);
    if (signedIn) { paintAvatar(m.profile); writeMarker(m.profile, m.role); } else avatarFor = null;
}

// ── Avatar: the photo when there is one (api.avatarUrl, added with
// pages/profile.js), else the initials. Never blocks paint: the initials show
// first and the photo swaps in when its URL resolves. One lookup per profile
// object (api.me() caches it; a profile edit brings a new object, a new lookup).
const photoUrls = new WeakMap();
function photoOf(p) {
    if (typeof api.avatarUrl !== 'function') return Promise.resolve(null);
    if (!photoUrls.has(p)) photoUrls.set(p, Promise.resolve().then(() => api.avatarUrl(p)).catch(() => null));
    return photoUrls.get(p);
}
let avatarFor = null;
function paintAvatar(p) {
    const a = document.getElementById('meBtn');
    const who = String(p.id ?? p.email ?? '');
    const name = p.preferred_name || p.full_name || p.email || '';
    a.setAttribute('aria-label', name ? `Профіль: ${name}` : 'Профіль');
    avatarFor = p;
    const showInitials = () => { a.innerHTML = `<span>${esc(initials(p))}</span>`; };
    // Same person, photo already shown: keep it (no flash on every route).
    if (!(a.dataset.for === who && a.querySelector('img'))) showInitials();
    a.dataset.for = who;
    crowned(p).then((c) => { if (avatarFor === p) a.classList.toggle('is-crowned', c); });
    photoOf(p).then((url) => {
        if (avatarFor !== p) return;
        if (!url) { if (a.querySelector('img')) showInitials(); return; }
        if (a.querySelector('img')?.getAttribute('src') === url) return;
        const img = new Image(36, 36);
        img.alt = ''; img.decoding = 'async';
        img.onerror = () => { if (avatarFor === p) showInitials(); };
        img.src = url;
        a.replaceChildren(img);
    });
}

// ── The public site's «signed in» marker (localStorage `apex.me`): its header
// shows «Навчання» (`r`: a teacher's «Викладання», an admin's «Управління»)
// and this avatar from it,
// without loading Firebase. `p` is a
// photo only when its URL never expires (api.avatarMarker, else a data: URL):
// a signed storage URL would break on the public page within the hour.
const markerPhotos = new WeakMap();
function writeMarker(p, r) {
    const n = p.preferred_name || p.full_name || '';
    const put = (ph) => {
        try { localStorage.setItem('apex.me', JSON.stringify({ n, i: initials(p), p: ph || null, r, c: crown || undefined, t: Date.now() })); } catch {}
    };
    let crown = false;   // `c`: the crown (ui.js crowned), once the check answers
    crowned(p).then((c) => { if (c && avatarFor === p) { crown = true; put(markerPhoto); } });
    // Keep this person's last photo until the lookup answers.
    let prev = null;
    try { const o = JSON.parse(localStorage.getItem('apex.me') || 'null'); if (o && o.n === n) prev = o.p || null; } catch {}
    let markerPhoto = prev;
    put(prev);
    if (!markerPhotos.has(p)) {
        markerPhotos.set(p, typeof api.avatarMarker === 'function'
            ? Promise.resolve().then(() => api.avatarMarker(p)).catch(() => null)
            : photoOf(p).then((u) => (/^data:image\//.test(u || '') ? u : null)));
    }
    markerPhotos.get(p).then((ph) => { if (avatarFor === p) put(markerPhoto = ph); }).catch(() => {});
}
function forgetMarker() {
    avatarFor = null;
    try { localStorage.removeItem('apex.me'); } catch {}
}

// The staff tab bar's «Ще» opens this menu as a bottom sheet, within thumb reach.
function openMenu(m, extra, sheet) {
    const menu = document.getElementById('meMenu');
    const btn = document.getElementById('moreBtn');
    if (!menu.hidden) { menu.hidden = true; btn?.setAttribute('aria-expanded', 'false'); return; }
    menu.classList.toggle('is-sheet', !!sheet);
    menu.innerHTML = `<p class="me-who"><b>${esc(nick(m.profile))}</b><br><span class="fine">${esc(m.profile.email || '')}</span></p>
      ${extra.map(([href, label, icon]) => `<a href="${href}" data-track="none">${svg(icon)}<span>${esc(label)}</span></a>`).join('')}
      <a href="/" data-track="none">${svg('home')}<span>Архів задач</span></a>
      ${api.hasDemo() ? '<a href="/cabinet/login?switch=1" data-track="none">' + svg('people') + '<span>Інший демо-користувач</span></a>' : ''}
      <button type="button" id="signOut" data-track="none">${svg('door')}<span>Вийти</span></button>`;
    menu.hidden = false;
    btn?.setAttribute('aria-expanded', 'true');
    const close = (e) => { if (!menu.contains(e.target) && !e.target.closest('#moreBtn')) { menu.hidden = true; btn?.setAttribute('aria-expanded', 'false'); document.removeEventListener('click', close, true); } };
    setTimeout(() => document.addEventListener('click', close, true));
    menu.querySelectorAll('a').forEach(a => a.addEventListener('click', () => { menu.hidden = true; }));
    menu.querySelector('#signOut').addEventListener('click', async () => {
        await api.signOut(); forgetMarker(); menu.hidden = true; toast('Ви вийшли'); navigate('/cabinet/login');
    });
}

window.addEventListener('popstate', route);
// Homework photos waiting on the phone go up when the network comes back.
window.addEventListener('online', () => api.flushHomeworkQueue().then(n => n && toast('Фото домашньої надіслано')));
setTimeout(() => api.flushHomeworkQueue().catch(() => {}), 4000);
window.addEventListener('cabinet:route', route);
// A repaint of the header after points or a profile edit.
window.addEventListener('cabinet:points', async () => paint(await api.me()));
loading();
route();
